import {
  IconCloseRounded,
  IconDownload,
  IconPen,
  IconPlusRounded,
  IconTickRounded,
  IconTrash,
  IconUpload,
} from "@aksara-ui/icons";
import {
  Badge,
  Box,
  Button,
  Card,
  Circle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogProps,
  IconButton,
  InputText,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllCatalogStocks, importCatalogStock } from "redux/actions";
import {
  CatalogStockAddModal,
  CatalogStockConfirmationModal,
  CatalogStockUpdateModal,
} from "./components";
import { InputSearchbox } from "components/Form/InputSearchbox";
import { Pagination, PaginationFilter } from "components/Pagination";
import moment from "moment";

interface Props {
  catalogId: string;
  type: string;
}

const CatalogStocksContent: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  catalogId,
  type,
}) => {
  const dispatch = useDispatch();
  const importFileExcelInput = useRef<any>(null);

  const { isLoadingModal } = useSelector((state: any) => state.globalReducer);
  const { catalogStocks, isImportLoading } = useSelector(
    (state: any) => state.catalogStockReducer
  );
  const { currentPage, totalPage, limitPage } = useSelector(
    (state: any) => state.catalogStockReducer
  );

  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);
  const [selectedCatalogStockId, setSelectedCatalogStockId] = useState("");
  const [selectedOpenAddModal, setSelectedOpenAddModal] = useState(false);
  const [selectedOpenUpdateModal, setSelectedOpenUpdateModal] = useState(false);
  const [selectedOpenConfirmationModal, setSelectedOpenConfirmationModal] =
    useState(false);

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    dispatch(
      getAllCatalogStocks(catalogId, 1, event.target.value, limitPage) as any
    );
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(getAllCatalogStocks(catalogId, currentPage, "", limitPage) as any);
  };

  const submitImportFileExcel = () => {
    importFileExcelInput.current!.click();
  };

  const handleChangeImportFileExcel = (event: any) => {
    const importFile = event.target.files[0];
    dispatch(importCatalogStock(catalogId, importFile) as any);

    event.target.value = null;
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/assets/doc/template/Template - CGV Voucher - CMS.xlsx";
    link.download = "Template - CGV Voucher - CMS.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenAddModal = () => {
    setSelectedOpenAddModal(true);
  };

  const handleCloseAddModal = () => {
    setSelectedOpenAddModal(false);
  };

  const handleOpenUpdateModal = (catalogStockId: string = "") => {
    setSelectedOpenUpdateModal(true);
    setSelectedCatalogStockId(catalogStockId);
  };

  const handleCloseUpdateModal = () => {
    setSelectedOpenUpdateModal(false);
    setSelectedCatalogStockId("");
  };

  const handleOpenConfirmationModal = (catalogStockId: string = "") => {
    setSelectedOpenConfirmationModal(true);
    setSelectedCatalogStockId(catalogStockId);
  };

  const handleCloseConfirmationModal = () => {
    setSelectedOpenConfirmationModal(false);
    setSelectedCatalogStockId("");
  };

  const handleCloseModal = () => {
    onClose && onClose();
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllCatalogStocks(catalogId) as any);
    }
  }, [isOpen, dispatch, catalogId]);

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
      minWidth={type === "Voucher" ? "40vw" : "30vw"}
      maxWidth={type === "Voucher" ? "50vw" : "30vw"}
      maxHeight={"90vh"}
      style={{
        height: "80vh",
        display: "flex",
        alignContent: "center",
      }}
    >
      <DialogHeader>
        <Text fontWeight={700}>{type} Stocks</Text>
      </DialogHeader>
      <DialogContent>
        <Box
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"space-between"}
          paddingBottom={15}
          style={{ gap: 20 }}
        >
          <InputSearchbox
            autoComplete="off"
            name="textDummy"
            placeholder="Search..."
            size={32}
            onChange={handleSearchChange}
            onClearButton={handleClearSearchChange}
          />

          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent={type === "Voucher" ? "space-between" : "right"}
            style={{ gap: 10 }}
          >
            {type === "Voucher" ? (
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                style={{
                  gap: 10,
                }}
              >
                <Button
                  size={32}
                  variant="outline"
                  borderRadius={20}
                  style={{
                    color: "white",
                    backgroundColor: `${theme.colors.green07}`,
                    fontWeight: 600,
                    fontSize: 12,
                    gap: 5,
                  }}
                  display={"flex"}
                  alignItems={"center"}
                  iconPosition="left"
                  onClick={() => handleDownloadTemplate()}
                >
                  <IconDownload size={20} fill={"white"} /> Download Template
                </Button>
                <Box>
                  <Button
                    size={32}
                    borderRadius={20}
                    style={{
                      backgroundColor: `${theme.colors.grey02}`,
                      borderColor: `${theme.colors.grey04}`,
                      fontWeight: 600,
                      fontSize: 12,
                      gap: 5,
                    }}
                    display={"flex"}
                    alignItems={"center"}
                    iconPosition="left"
                    onClick={submitImportFileExcel}
                  >
                    {isImportLoading ? (
                      <Circle size={20} />
                    ) : (
                      <IconUpload size={20} fill={"currentColor"} />
                    )}{" "}
                    Export Excel
                  </Button>
                  <InputText
                    type="file"
                    ref={importFileExcelInput}
                    onChange={handleChangeImportFileExcel}
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    style={{ display: "none" }}
                  />
                </Box>
              </Box>
            ) : (
              <></>
            )}

            <Button
              size={32}
              variant="primary"
              borderRadius={20}
              style={{
                fontWeight: 600,
                fontSize: 12,
                gap: 5,
              }}
              display={"flex"}
              alignItems={"center"}
              iconPosition="left"
              onClick={() => handleOpenAddModal()}
            >
              <IconPlusRounded size={20} fill={"white"} /> Add {type} Stock
            </Button>
          </Box>
        </Box>

        <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
          <Table>
            <TableHead position={"sticky"} top={0} backgroundColor={"white"}>
              <TableHeadRow>
                <TableHeadCell textAlign={"center"}>Unique Code</TableHeadCell>

                {type === "Voucher" ? (
                  <TableHeadCell textAlign={"center"} minWidth={150}>
                    Pin
                  </TableHeadCell>
                ) : (
                  <></>
                )}
                {type === "Voucher" ? (
                  <TableHeadCell textAlign={"center"} minWidth={150}>
                    Expiry Date
                  </TableHeadCell>
                ) : (
                  <></>
                )}
                <TableHeadCell textAlign={"center"}>
                  Stock Available
                </TableHeadCell>
                <TableHeadCell textAlign={"center"} style={{ width: 120 }}>
                  Action
                </TableHeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {isLoadingModal ? (
                <TableBodyRowsSkeleton
                  rowsNumber={10}
                  columnsNumber={type === "Voucher" ? 5 : 3}
                />
              ) : !catalogStocks.length ? (
                <TableBodyRow>
                  <TableBodyCell
                    colSpan={type === "Voucher" ? 5 : 3}
                    textAlign={"center"}
                  >
                    No stock available
                  </TableBodyCell>
                </TableBodyRow>
              ) : (
                catalogStocks.map((catalogStock: any) => {
                  return (
                    <TableBodyRow
                      key={catalogStock.id}
                      backgroundColor={
                        catalogStock.user_id ? "#f5f6fa" : theme.colors.white
                      }
                    >
                      <TableBodyCell whiteSpace={"nowrap"} textAlign={"center"}>
                        <Badge
                          size="lg"
                          variant="info"
                          style={{
                            paddingLeft: 20,
                            paddingRight: 20,
                          }}
                        >
                          <Text fontWeight={700}>
                            {catalogStock.unique_code}
                          </Text>
                        </Badge>
                      </TableBodyCell>
                      {type === "Voucher" ? (
                        <TableBodyCell textAlign={"center"}>
                          {catalogStock.pin ? "******" : "-"}
                        </TableBodyCell>
                      ) : (
                        <></>
                      )}
                      {type === "Voucher" ? (
                        <TableBodyCell textAlign={"center"}>
                          {moment(catalogStock.expiry_date).format(
                            "DD MMM YYYY"
                          )}
                        </TableBodyCell>
                      ) : (
                        <></>
                      )}
                      <TableBodyCell whiteSpace={"nowrap"} textAlign={"center"}>
                        {catalogStock.user_id ? (
                          <IconCloseRounded fill={theme.colors.red06} />
                        ) : (
                          <IconTickRounded fill={theme.colors.green06} />
                        )}
                      </TableBodyCell>
                      <TableBodyCell textAlign={"center"}>
                        <IconButton
                          onClick={() => handleOpenUpdateModal(catalogStock.id)}
                          disabled={!!catalogStock.user_id}
                        >
                          <IconPen
                            fill={
                              catalogStock.user_id
                                ? "#95a5a6"
                                : theme.colors.green06
                            }
                          />
                        </IconButton>
                        <IconButton
                          onClick={() =>
                            handleOpenConfirmationModal(catalogStock.id)
                          }
                          disabled={!!catalogStock.user_id}
                        >
                          <IconTrash
                            fill={
                              catalogStock.user_id
                                ? "#95a5a6"
                                : theme.colors.red06
                            }
                          />
                        </IconButton>
                      </TableBodyCell>
                    </TableBodyRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          paddingY={10}
        >
          <Pagination
            current={currentPage}
            total={totalPage}
            onSelect={(select: any) =>
              dispatch(
                getAllCatalogStocks(
                  catalogId,
                  select,
                  searchValue,
                  limitPage
                ) as any
              )
            }
          />
          <PaginationFilter
            selectedItem={limit}
            onChange={(selectedItem) => {
              if (selectedItem) {
                setLimit(selectedItem);
                dispatch(
                  getAllCatalogStocks(
                    catalogId,
                    1,
                    searchValue,
                    selectedItem
                  ) as any
                );
              }
            }}
            items={[10, 20, 30, 40, 50]}
          />
        </Box>

        <CatalogStockAddModal
          catalogId={catalogId}
          type={type}
          isOpen={selectedOpenAddModal}
          onClose={() => handleCloseAddModal()}
        />

        <CatalogStockUpdateModal
          catalogId={catalogId}
          type={type}
          catalogStockId={selectedCatalogStockId}
          isOpen={selectedOpenUpdateModal}
          onClose={() => handleCloseUpdateModal()}
        />

        <CatalogStockConfirmationModal
          catalogId={catalogId}
          type={type}
          catalogStockId={selectedCatalogStockId}
          isOpen={selectedOpenConfirmationModal}
          onClose={() => handleCloseConfirmationModal()}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CatalogStocksContent;
